import { createAsyncThunk } from "@reduxjs/toolkit";
import { SchedulePreferencesResponse } from "../contracts/SchedulePreferencesResponse";
import { SchedulePreferences } from "../models/SchedulePreferences";
import {
  mapPreferencesToRequest,
  mapResponseToPreferences,
} from "../mappers/SchedulePreferencesMapper";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";

export const fetchSchedulePreferences = createAsyncThunk<
  SchedulePreferences,
  void,
  { rejectValue: ApiErrorResponse }
>("instructorPreferences/fetch", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<SchedulePreferencesResponse>(
      "/instructor/preferences"
    );
    return mapResponseToPreferences(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load preferences"));
  }
});

export const saveSchedulePreferences = createAsyncThunk<
  SchedulePreferences,
  SchedulePreferences,
  { rejectValue: ApiErrorResponse }
>("instructorPreferences/save", async (preferences, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<SchedulePreferencesResponse>(
      "/instructor/preferences",
      mapPreferencesToRequest(preferences)
    );
    return mapResponseToPreferences(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to save preferences"));
  }
});

/**
 * Sends calendar invites for every active session with upcoming occurrences.
 * Dispatched right after the calendar-invites preference flips on, so the
 * instructor's calendar immediately reflects the existing schedule.
 * Resolves to the number of invites sent.
 */
export const backfillCalendarInvites = createAsyncThunk<
  number,
  void,
  { rejectValue: ApiErrorResponse }
>("instructorPreferences/backfillCalendarInvites", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<{ sent: number }>(
      "/bookings/calendar-invites/backfill"
    );
    return response.data.sent;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to send calendar invites"));
  }
});
